import React, {FunctionComponent, ReactNode, useEffect, useState} from 'react';
import { Translate } from 'react-localize-redux';
import DateTimeFormatter, { DateTimeFormat } from '../../../common/components/DateTimeFormatter';
import { getLoanStatusTranslateKey } from '../../../common/helpers/loanStatusFunctions';
import { LoanStatus } from '../../../common/models/LoanStatus';
import { ConsumerLoanBrief } from '../models/ConsumerLoanBrief';
import './loan-picker.css';
import { LazyScrollContainer } from '../../../common/components/LazyScrollContainer';
import { loanStatusToColor } from '../../../common/helpers/loanStatusToColor';

interface LoanPickerProps {
    loans: ConsumerLoanBrief[];
    selectedLoanId?: number;
    scopeSourceId?: number | null;
    countLimit?: number;
    onLoanPicked?(loanId: number): void;
}

interface LoanPickerState {
    maxCount: number;
}

const defaultCountLimit: number = 5;
const itemWidth: number = 97;
const buttonWidth: number = 29;

function compareLoans(loan1: ConsumerLoanBrief, loan2: ConsumerLoanBrief) {
    if (loan1.submittedDate === loan2.submittedDate) {
        return loan2.id - loan1.id;
    }

    return new Date(loan2.submittedDate).valueOf() - new Date(loan1.submittedDate).valueOf();
}

const LoanPicker: FunctionComponent<LoanPickerProps> = (props) => {
    const [mainElement, setMainElement] = useState<HTMLDivElement | null>(null);
    const [state, setState] = useState<LoanPickerState>({maxCount: 0});
    
    useEffect(() => {
        window.addEventListener('resize', updateDimensions);
        return () => window.removeEventListener('resize', updateDimensions);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const loans = props.loans.sort(compareLoans);
    const initialPosition = props.selectedLoanId === undefined ? 0 : loans.map((l) => l.id).indexOf(props.selectedLoanId);

    function updateDimensions() {
        if (mainElement != null) {
            const availableCount = Math.floor((mainElement.offsetWidth - 2 * buttonWidth) / itemWidth);
            const maxCount = Math.min(availableCount, props.countLimit || defaultCountLimit);
            if (maxCount !== state.maxCount) {
                setState({ maxCount });
            }
        }
    }

    function refCallback(element: HTMLDivElement | null) {
        setMainElement(element);
        updateDimensions();
    }

    return (
        <div ref={refCallback} className="loan-picker">
            <LazyScrollContainer 
                items={loans} 
                renderItem={(loan, index) => mapLoan(props, loan, index)} 
                maxCount={state.maxCount}
                initialPosition={initialPosition}
            />
        </div>
    );
}

function mapLoan(props: LoanPickerProps, loan: ConsumerLoanBrief, index: number): ReactNode {
    const clickable = props.onLoanPicked && (props.scopeSourceId === undefined || props.scopeSourceId === null || props.scopeSourceId === loan.sourceId);
    const clickHandler = clickable ? () => props.onLoanPicked && props.onLoanPicked(loan.id) : undefined;

    const statusClass = (loanStatus: LoanStatus): string => {
        return `status${loanStatusToColor(loanStatus, ' status-', loan.applicationFormProgress)}`;
    };

    let loanPickerItemClasses = loan.id === props.selectedLoanId ? 'loan-picker-item selected' : 'loan-picker-item';

    if (loan.hidden) {
        loanPickerItemClasses += ' hidden';
    }

    return (
        <div className={loanPickerItemClasses} key={`loan-${index}`} onClick={clickHandler} style={{minWidth: `${itemWidth}px`, cursor: clickable ? 'pointer' : 'default'}}>
            <div className={statusClass(loan.loanStatus)}>
                <Translate id={getLoanStatusTranslateKey(loan.loanStatus)} />
            </div>
            <div className="date-container">
                <DateTimeFormatter date={loan.submittedDate} format={DateTimeFormat.Date} />
            </div>
        </div>
    );
}
export default LoanPicker;
